import type {
  AmplitudeConfig,
  AmplitudeEngagementConfig,
  AmplitudeErrorReporter,
  AmplitudeServerZone,
  AmplitudeSessionReplayConfig,
  SessionReplayPluginModule,
} from './config'

/** Long-lived tabs: without it a guide published in Amplitude shows after a reload only. */
export const GUIDES_AUTO_REFRESH_SECONDS = 600

/** Nothing recorded and everything masked until Amplitude's remote config says otherwise. */
export const SESSION_REPLAY_FLOOR: AmplitudeSessionReplayConfig = {
  sampleRate: 0,
  privacyConfig: { defaultMaskLevel: 'conservative' },
}

interface B2bArgs {
  apiKey: string | undefined
  serverZone?: AmplitudeServerZone
  venueId?: string
  guidesAndSurveys?: AmplitudeEngagementConfig
  loadSessionReplayPlugin?: () => Promise<SessionReplayPluginModule>
  sessionReplay?: AmplitudeSessionReplayConfig
  onError?: AmplitudeErrorReporter
}

/** Providers are tracked under contract: always on, the venue is the user. */
export const b2bAmplitudeConfig = ({
  apiKey,
  serverZone,
  venueId,
  guidesAndSurveys,
  loadSessionReplayPlugin,
  sessionReplay,
  onError,
}: B2bArgs): AmplitudeConfig | undefined => {
  if (!apiKey) return undefined

  return {
    apiKey,
    serverZone,
    userId: venueId,
    engagement: guidesAndSurveys && { autoRefreshIntervalSeconds: GUIDES_AUTO_REFRESH_SECONDS, ...guidesAndSurveys },
    loadSessionReplayPlugin,
    sessionReplay,
    onError,
  }
}

type B2cArgs = Omit<B2bArgs, 'venueId'>

/** Consumers are never identified; consent is the provider's `dnt-local-storage` strategy. */
export const b2cAmplitudeConfig = ({
  apiKey,
  serverZone,
  guidesAndSurveys,
  loadSessionReplayPlugin,
  sessionReplay,
  onError,
}: B2cArgs): AmplitudeConfig | undefined => {
  if (!apiKey) return undefined

  return {
    apiKey,
    serverZone,
    engagement: guidesAndSurveys,
    loadSessionReplayPlugin,
    sessionReplay,
    onError,
  }
}
